import {
  Avatar,
  Box,
  BoxProps,
  IconButton,
  LightMode,
  Menu,
  MenuButton,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { QueryNodeUtils } from '@helo/cms'
import LZString from 'lz-string'
import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { internalDirectQueryDevtoolsContextQuery } from '../../queries/graphql/graphqlQueries'
import { apolloClient } from '../../services/apolloClient'
import { useAppStore } from '../../store/appStore/appStore'
import { Icon } from '../Icons/Icon'
import { ProfileIcon } from '../Icons/ProfileIcon'
import { SignOutIcon } from '../Icons/SignOutIcon'
import { ProfileMenuItem } from './ProfileMenuItem'

const ProfileMenuIconCircle = ({ children, ...props }: BoxProps) => {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      border="1px solid"
      borderColor="presets.turquoise.60"
      borderRadius="50%"
      width="32px"
      height="32px"
      {...props}
    >
      {children}
    </Box>
  )
}

type ProfileMenuProps = BoxProps & {}

/** `dbSubgraphsUrl` from the API is base only; Electrum GraphQL lives at this path. */
const electrumPythonGraphqlHref = (dbSubgraphsBaseUrl: string): string => {
  const base = dbSubgraphsBaseUrl.trim()
  const normalizedBase = base.endsWith('/') ? base : `${base}/`
  return new URL('electrum-python', normalizedBase).href
}

type DirectQueryContext = {
  internalToken: string
  dbSubgraphsUrl: string
}

type InternalDirectQueryDevtoolsContextResponse = {
  internalToken: string
  dbSubgraphsUrl: string
}

const internalDirectQueryDevtoolsContextOperation =
  internalDirectQueryDevtoolsContextQuery as TypedDocumentNode<
    InternalDirectQueryDevtoolsContextResponse,
    Record<string, never>
  >

export const ProfileMenu = ({ ...props }: ProfileMenuProps) => {
  const { logout, hasRole, user, token, queries, apiUrl } = useAppStore(
    ({ auth, queryStore, config }) => ({
      logout: auth.logout,
      hasRole: auth.hasRole,
      user: auth.user,
      token: auth.token,
      queries: queryStore.queries,
      apiUrl: config.API_URL,
    }),
  )

  const isHeloInternal = hasRole(['Helo-Internal'])
  const [directQuery, setDirectQuery] = useState<DirectQueryContext | null>(
    null,
  )

  const initialsSingleName = user?.name?.substring(0, 2).toUpperCase()

  const handleProfileMenuOpen = useCallback(() => {
    if (!isHeloInternal || directQuery) return
    void apolloClient
      .query<InternalDirectQueryDevtoolsContextResponse, Record<string, never>>(
        {
          query: internalDirectQueryDevtoolsContextOperation,
          fetchPolicy: 'network-only',
        },
      )
      .then(({ data }) => {
        if (data?.internalToken && data?.dbSubgraphsUrl) {
          setDirectQuery({
            internalToken: data.internalToken,
            dbSubgraphsUrl: data.dbSubgraphsUrl,
          })
        }
      })
      .catch((err: unknown) => {
        console.error('ProfileMenu: direct query devtools context', err)
      })
  }, [directQuery, isHeloInternal])

  // Helper function to build GraphQL Playground URL
  const buildPlaygroundUrl = useMemo(() => {
    return (options?: {
      document?: string
      variables?: Record<string, unknown>
    }) => {
      const params = new URLSearchParams()

      if (token) {
        params.set(
          'headers',
          JSON.stringify({ Authorization: `Bearer ${token}` }),
        )
      }

      // Only add explorerURLState if document is provided
      if (options?.document) {
        const explorerURLState = LZString.compressToEncodedURIComponent(
          JSON.stringify({
            document: options.document,
            variables: JSON.stringify(options.variables ?? {}),
          }),
        )
        params.set('explorerURLState', explorerURLState)
      }

      const queryString = params.toString()
      return queryString
        ? `${apiUrl}/graphql?${queryString}`
        : `${apiUrl}/graphql`
    }
  }, [token, apiUrl])

  const buildDirectQueryPlaygroundUrl = useMemo(() => {
    return (options?: {
      document?: string
      variables?: Record<string, unknown>
    }) => {
      if (!directQuery) {
        return '#'
      }
      const params = new URLSearchParams()
      params.set(
        'headers',
        JSON.stringify({
          Authorization: `Bearer ${directQuery.internalToken}`,
        }),
      )
      if (options?.document) {
        const explorerURLState = LZString.compressToEncodedURIComponent(
          JSON.stringify({
            document: options.document,
            variables: JSON.stringify(options.variables ?? {}),
          }),
        )
        params.set('explorerURLState', explorerURLState)
      }
      const queryString = params.toString()
      const base = electrumPythonGraphqlHref(directQuery.dbSubgraphsUrl)
      return queryString ? `${base}?${queryString}` : base
    }
  }, [directQuery])

  // Get array of query entries for rendering
  const queryEntries = useMemo(() => {
    return Object.entries(queries).map(([queryId, queryNode]) => {
      // Get variables and apply permanent filters (same pattern as internal query execution)
      const baseVariables = QueryNodeUtils.getVariablesAsObject({
        prune: true,
        pruneTopLevel: false,
      })(queryNode)

      // Apply permanent filters if they exist
      const applyPermanentFilters = (
        QueryNodeUtils as unknown as {
          applyPermanentFilters: (
            qn: typeof queryNode,
          ) => (vars: Record<string, unknown>) => Record<string, unknown>
        }
      ).applyPermanentFilters

      const variables = applyPermanentFilters
        ? applyPermanentFilters(queryNode)(baseVariables)
        : baseVariables

      return {
        queryId,
        queryNode,
        url: buildPlaygroundUrl({
          document: queryNode.queryDefinition.rawQuery,
          variables,
        }),
        directUrl: buildDirectQueryPlaygroundUrl({
          document: queryNode.queryDefinition.rawQuery,
          variables,
        }),
      }
    })
  }, [queries, buildPlaygroundUrl, buildDirectQueryPlaygroundUrl])

  const hasQueries = queryEntries.length > 0

  // Base playground URL (headers only, no query)
  const basePlaygroundUrl = buildPlaygroundUrl()
  const baseDirectQueryUrl = buildDirectQueryPlaygroundUrl()

  return (
    <Menu
      variant="rounded"
      autoSelect={false}
      onOpen={handleProfileMenuOpen}
      {...props}
    >
      <MenuButton
        as={IconButton}
        icon={
          <Avatar
            name={
              user?.name?.includes(' ')
                ? user?.name
                : initialsSingleName?.split('').join(' ')
            }
            _dark={{
              color: 'white',
              background: 'none',
            }}
          />
        }
        aria-label="profile"
        ml="4px"
        _active={{
          backgroundColor: 'presets.turquoise.10',
        }}
        border="1px solid"
        padding="0"
        minWidth="none"
      />
      <LightMode>
        <MenuList
          mt="1"
          minWidth="150px"
          p="16px 8px"
          border="none"
          shadow="drawer.notifications"
        >
          <ProfileMenuItem
            icon={
              <ProfileMenuIconCircle>
                <ProfileIcon boxSize="20px" color="presets.turquoise.60" />
              </ProfileMenuIconCircle>
            }
          >
            <VStack spacing={1} align="start">
              <Text fontSize="12px" fontWeight="600" lineHeight="15px">
                {user?.name}
              </Text>
              <Text
                fontSize="10px"
                fontWeight="600"
                lineHeight="12px"
                color="presets.neutral.50"
              >
                {user?.email}
              </Text>
            </VStack>
          </ProfileMenuItem>
          <ProfileMenuItem
            as={Link}
            //@ts-ignore
            to="/settings"
            icon={
              <ProfileMenuIconCircle borderColor="transparent">
                <Icon icon="GearIcon" boxSize="20px" />
              </ProfileMenuIconCircle>
            }
          >
            <Text fontSize="16px" fontWeight="500">
              Settings
            </Text>
          </ProfileMenuItem>
          <ProfileMenuItem
            as={Link}
            //@ts-ignore
            to="/user-guide"
            icon={
              <ProfileMenuIconCircle borderColor="transparent">
                <Icon icon="InfoOutlineIcon" boxSize="24px" />
              </ProfileMenuIconCircle>
            }
          >
            <Text fontSize="16px" fontWeight="500">
              User Guide
            </Text>
          </ProfileMenuItem>
          {isHeloInternal && (
            <ProfileMenuItem
              as={Link}
              //@ts-ignore
              to="/admin"
              icon={
                <ProfileMenuIconCircle borderColor="transparent">
                  <Icon icon="ProfileIcon" boxSize="20px" />
                </ProfileMenuIconCircle>
              }
            >
              <Text fontSize="16px" fontWeight="500">
                Admin
              </Text>
            </ProfileMenuItem>
          )}
          {isHeloInternal && !hasQueries && (
            <ProfileMenuItem
              as="a"
              //@ts-ignore
              href={basePlaygroundUrl}
              target="_blank"
              icon={
                <ProfileMenuIconCircle borderColor="transparent">
                  <Icon icon="GraphQLIcon" boxSize="20px" />
                </ProfileMenuIconCircle>
              }
            >
              <Text fontSize="16px" fontWeight="500">
                GraphQL Playground
              </Text>
            </ProfileMenuItem>
          )}
          {isHeloInternal && hasQueries && (
            <Menu placement="left-start">
              <MenuButton
                as={Box}
                cursor="pointer"
                width="100%"
                _hover={{ bg: 'presets.neutral.20' }}
                borderRadius="32px"
              >
                <ProfileMenuItem
                  icon={
                    <ProfileMenuIconCircle borderColor="transparent">
                      <Icon icon="GraphQLIcon" boxSize="20px" />
                    </ProfileMenuIconCircle>
                  }
                  closeOnSelect={false}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      GraphQL Playground
                    </Text>
                    <Icon
                      icon="ChevronIcon"
                      boxSize="16px"
                      transform="rotate(-90deg)"
                    />
                  </Box>
                </ProfileMenuItem>
              </MenuButton>
              <MenuList
                minWidth="200px"
                maxWidth="300px"
                border="none"
                shadow="drawer.notifications"
                p="8px"
              >
                <ProfileMenuItem
                  as="a"
                  //@ts-ignore
                  href={basePlaygroundUrl}
                  target="_blank"
                >
                  <Text fontSize="14px" fontWeight="500">
                    GraphQL Playground
                  </Text>
                </ProfileMenuItem>
                {queryEntries.map(({ queryId, url }) => (
                  <ProfileMenuItem
                    key={queryId}
                    as="a"
                    //@ts-ignore
                    href={url}
                    target="_blank"
                  >
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      noOfLines={1}
                      title={queryId}
                    >
                      {queryId}
                    </Text>
                  </ProfileMenuItem>
                ))}
              </MenuList>
            </Menu>
          )}
          {isHeloInternal && directQuery && !hasQueries && (
            <ProfileMenuItem
              as="a"
              //@ts-ignore
              href={baseDirectQueryUrl}
              target="_blank"
              icon={
                <ProfileMenuIconCircle borderColor="transparent">
                  <Icon icon="GraphQLIcon" boxSize="20px" />
                </ProfileMenuIconCircle>
              }
            >
              <Text fontSize="16px" fontWeight="500">
                Direct query
              </Text>
            </ProfileMenuItem>
          )}
          {isHeloInternal && directQuery && hasQueries && (
            <Menu placement="left-start">
              <MenuButton
                as={Box}
                cursor="pointer"
                width="100%"
                _hover={{ bg: 'presets.neutral.20' }}
                borderRadius="32px"
              >
                <ProfileMenuItem
                  icon={
                    <ProfileMenuIconCircle borderColor="transparent">
                      <Icon icon="GraphQLIcon" boxSize="20px" />
                    </ProfileMenuIconCircle>
                  }
                  closeOnSelect={false}
                >
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                  >
                    <Text fontSize="16px" fontWeight="500">
                      Direct query
                    </Text>
                    <Icon
                      icon="ChevronIcon"
                      boxSize="16px"
                      transform="rotate(-90deg)"
                    />
                  </Box>
                </ProfileMenuItem>
              </MenuButton>
              <MenuList
                minWidth="200px"
                maxWidth="300px"
                border="none"
                shadow="drawer.notifications"
                p="8px"
              >
                <ProfileMenuItem
                  as="a"
                  //@ts-ignore
                  href={baseDirectQueryUrl}
                  target="_blank"
                >
                  <Text fontSize="14px" fontWeight="500">
                    Direct query
                  </Text>
                </ProfileMenuItem>
                {queryEntries.map(({ queryId, directUrl }) => (
                  <ProfileMenuItem
                    key={queryId}
                    as="a"
                    //@ts-ignore
                    href={directUrl}
                    target="_blank"
                  >
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      noOfLines={1}
                      title={queryId}
                    >
                      {queryId}
                    </Text>
                  </ProfileMenuItem>
                ))}
              </MenuList>
            </Menu>
          )}
          {isHeloInternal && (
            <ProfileMenuItem
              as="a"
              //@ts-ignore
              href="/storybook/"
              target="_blank"
              icon={
                <ProfileMenuIconCircle borderColor="transparent">
                  <Icon icon="StorybookIcon" boxSize="20px" />
                </ProfileMenuIconCircle>
              }
            >
              <Text fontSize="16px" fontWeight="500">
                Storybook
              </Text>
            </ProfileMenuItem>
          )}
          <ProfileMenuItem
            onClick={logout}
            icon={
              <ProfileMenuIconCircle borderColor="transparent">
                <SignOutIcon transform="rotate(90deg)" boxSize="20px" />
              </ProfileMenuIconCircle>
            }
          >
            <Text fontSize="16px" fontWeight="500">
              Sign Out
            </Text>
          </ProfileMenuItem>
        </MenuList>
      </LightMode>
    </Menu>
  )
}
